import React, { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Collapse,
    IconButton,
    Paper,
    Box,
} from "@mui/material";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import CustomTablePagination from "../../../components/CustomTablePagination/CustomTablePagination";
import { Link } from "react-router-dom";
import styles from "./patient-survey-report.module.scss";

const ORDER_DETAILS_ROUTE_PREFIX = "/orders/order-details";

const sanitizeBranch = (s) =>
    (s ?? "")
        .replace(/[��]/g, "-")
        .replace(/[^\x20-\x7E]/g, "")
        .replace(/\s{2,}/g, " ")
        .trim();

const mapOrderTypeLabel = (code) => {
    switch ((code || "").trim()) {
        case "D":
            return "Delivery";
        case "P":
            return "Pickup";
        case "SR":
            return "Service";
        case "M":
            return "Mixed";
        default:
            return code || "-";
    }
};

const formatDate = (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

const isExpandable = (r) => {
    const rating = Number(r?.rating);
    const hasIssues = Array.isArray(r?.issues) && r.issues.length > 0;
    const hasFeedback = (r?.feedback ?? "").trim().length > 0;
    return rating <= 3 && (hasIssues || hasFeedback);
};

const renderIssueChip = (t, idx) => {
    const m = String(t).match(/^\s*([^:]+):\s*(.+)\s*$/);
    if (!m)
        return (
            <span key={idx} className={styles.issueChip}>
                <b>{t}</b>
            </span>
        );
    const [, label, text] = m;
    return (
        <span key={idx} className={styles.issueChip}>
            <span className={styles.issueChipLabel}>{label}:</span>{" "}
            <b className={styles.issueChipText}>{text}</b>
        </span>
    );
};

const PatientSurveyDetailsTable = ({
    rows = [],
    mode = "high",
    loading = false,
    page = 0,
    pageSize = 25,
    total = 0,
    onPageChange,
    onPageSizeChange,
}) => {
    const [openRows, setOpenRows] = useState({});
    const showExpandColumn = mode === "low";

    useEffect(() => {
        setOpenRows({});
    }, [rows]);

    const toggleRow = (index) => {
        setOpenRows((prev) => ({ ...prev, [index]: !prev[index] }));
    };

    const safePage = Number.isFinite(page) && page >= 0 ? page : 0;
    const safePageSize = Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 25;
    const safeTotal = Number.isFinite(total) ? total : rows.length;
    
    const columns = [
        "Respondent",
        "Date",
        "Order",
        "Branch",
        "Rating",
        ...(mode === "high"
            ? ["Feedback"]
            : ["Technician Arrival", "Equipment Condition", "Technician Service"]),
    ];

    return (
        <Box className={styles.detailsTableWrapper}>
            <TableContainer component={Paper}>
                <Table className={styles.borderNone}>
                    <TableHead className={styles.tableHead}>
                        <TableRow>
                            {showExpandColumn && (
                                <TableCell className={styles.tableIconCell}></TableCell>
                            )}
                            {columns.map((col, i) => (
                                <TableCell
                                    key={i}
                                    className={`${styles.tableCell} ${styles.tableCellPadding}`}
                                >
                                    {col}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {rows.map((row, idx) => {
                            const expandable = mode === "low" && isExpandable(row);
                            const isOpen = openRows[idx];
                            const altColor =
                                idx % 2 === 1 ? "var(--color-whitesmoke-100)" : "inherit";

                            return (
                                <React.Fragment key={idx}>
                                    <TableRow
                                        sx={{ backgroundColor: altColor }}
                                        className={styles.tableRow}
                                    >
                                        {showExpandColumn && (
                                            <TableCell className={styles.tableCellPadding}>
                                                {expandable && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => toggleRow(idx)}
                                                        aria-label={isOpen ? "collapse" : "expand"}
                                                    >
                                                        {isOpen ? (
                                                            <KeyboardArrowDownRoundedIcon />
                                                        ) : (
                                                            <KeyboardArrowRightRoundedIcon />
                                                        )}
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        )}

                                        <TableCell className={styles.tableCell}>
                                            {row.respondent}
                                        </TableCell>

                                        <TableCell className={styles.tableCell}>
                                            {formatDate(row.date)}
                                        </TableCell>

                                        <TableCell className={styles.tableCell}>
                                            <Link
                                                to={`${ORDER_DETAILS_ROUTE_PREFIX}/${row.order_id}`}
                                                onClick={(e) => e.stopPropagation()}
                                                className={styles.linkText}
                                            >
                                                <b>{row.order_id}</b>
                                            </Link>
                                            <div className={styles.orderSubtype}>
                                                {mapOrderTypeLabel(row.order_type)}
                                            </div>
                                        </TableCell>

                                        <TableCell className={styles.tableCell}>
                                            {sanitizeBranch(row.branch)}
                                        </TableCell>

                                        <TableCell className={styles.tableCell}>
                                            <Box className={`${styles.ratingPill} ${styles[`rating${row.rating}`]}`}>
                                                <StarRoundedIcon
                                                    fontSize="xs-small"
                                                    className={styles.ratingIcon}
                                                />
                                                <span>{row.rating}</span>
                                            </Box>
                                        </TableCell>

                                        {mode === "high" && (
                                            <TableCell className={styles.tableCell}>
                                                <div className={styles.feedbackBoxText}>
                                                    &quot;{row.feedback}&quot;
                                                </div>
                                            </TableCell>
                                        )}

                                        {mode === "low" && (
                                            <>
                                                <TableCell align="center" className={styles.tableCell}>
                                                    {(row.arrivalOK ?? row.arrival_ok) === true ? (
                                                        <CheckCircleRoundedIcon className={styles.badgeYes} />
                                                    ) : (row.arrivalOK ?? row.arrival_ok) === false ? (
                                                        <CancelRoundedIcon className={styles.badgeNo} />
                                                    ) : (
                                                        "-"
                                                    )}
                                                </TableCell>

                                                <TableCell align="center" className={styles.tableCell}>
                                                    {row.order_type?.trim() === "P" ? (
                                                        "-"
                                                    ) : (row.equipmentOK ?? row.equipment_ok) === true ? (
                                                        <CheckCircleRoundedIcon className={styles.badgeYes} />
                                                    ) : (row.equipmentOK ?? row.equipment_ok) === false ? (
                                                        <CancelRoundedIcon className={styles.badgeNo} />
                                                    ) : (
                                                        "-"
                                                    )}
                                                </TableCell>

                                                <TableCell align="center" className={styles.tableCell}>
                                                    {row.order_type?.trim() === "P" ? (
                                                        "-"
                                                    ) : (row.serviceOK ?? row.service_ok) === true ? (
                                                        <CheckCircleRoundedIcon className={styles.badgeYes} />
                                                    ) : (row.serviceOK ?? row.service_ok) === false ? (
                                                        <CancelRoundedIcon className={styles.badgeNo} />
                                                    ) : (
                                                        "-"
                                                    )}
                                                </TableCell>
                                            </>
                                        )}

                                    </TableRow>

                                    {expandable && (
                                        <TableRow className={styles.detailRow}>
                                            <TableCell
                                                style={{ paddingBottom: 0, paddingTop: 0 }}
                                                colSpan={columns.length + (showExpandColumn ? 1 : 0)}
                                            >
                                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                    <Paper elevation={0} className={styles.detailPanel}>
                                                        <div className={styles.detailPanelTitle}>
                                                            Issues and Feedback
                                                        </div>

                                                        {!!row.issues?.length && (
                                                            <div className={styles.issueChips}>
                                                                {row.issues
                                                                    .filter((issue) => {
                                                                        if (row.order_type?.trim() === "P") {
                                                                            return issue.toLowerCase().includes("arrival");
                                                                        }
                                                                        return true;
                                                                    })
                                                                    .map(renderIssueChip)}
                                                            </div>
                                                        )}
                                                        
                                                        {row.feedback && (
                                                            <div className={styles.feedbackBox}>
                                                                <ChatBubbleIcon
                                                                    fontSize="small"
                                                                    className={styles.feedbackIcon}
                                                                />
                                                                <span className={styles.feedbackBoxText}>
                                                                    &quot;{row.feedback}&quot;
                                                                </span>
                                                            </div>
                                                        )}
                                                    </Paper>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {!loading && (!rows || rows.length === 0) && (
                <Box className={styles.noDataBox}>
                    No data available
                </Box>
            )}

            <CustomTablePagination
                totalRecord={safeTotal}
                page={safePage}
                rowsPerPage={safePageSize}
                isLoading={loading}
                handleChangePage={(event, newPage) =>
                    onPageChange?.(Number.isFinite(newPage) ? newPage : 0)
                }
                handleChangeRowsPerPage={(event) => {
                    const next = parseInt(event.target.value, 10);
                    onPageSizeChange?.(Number.isFinite(next) && next > 0 ? next : 25);
                }}
            />
        </Box>
    );
};

export default PatientSurveyDetailsTable;
